import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { allPhotos, stories } from '../data/lookbook';
import { Icon, Ornament, Reveal } from '../components/Primitives';
import Lightbox from '../components/Lightbox';
import Seo from '../components/Seo';
import { CtaBand, PageHero } from '../components/Shared';

export default function Lookbook() {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const { hash } = useLocation();

  // Deep links like /lookbook#ivory land on that story and pre-select the filter.
  useEffect(() => {
    const slug = hash.replace('#', '');
    if (!slug) return;
    if (stories.some((s) => s.slug === slug)) setFilter(slug);
    const el = document.getElementById(slug);
    if (el) {
      window.requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      );
    }
  }, [hash]);

  const visible = useMemo(
    () => (filter === 'all' ? allPhotos : allPhotos.filter((p) => p.storySlug === filter)),
    [filter]
  );

  const tabs = [{ slug: 'all', title: 'Everything' }, ...stories];

  return (
    <>
      <Seo
        title="Lookbook"
        description="Editorial photography from Nikhil Jewellers — sapphire bridal polki, emerald and pearl on ivory, and contemporary diamond pendants."
        image="/assets/lookbook/sapphire-01-sm.webp"
      />

      <PageHero
        eyebrow="The Lookbook"
        title="Seen on the person wearing it"
        lede="Forty-one frames from three shoots. Tap any image to open it full size."
        image="/assets/lookbook/sapphire-w-01.webp"
        trail={[{ label: 'Home', to: '/' }, { label: 'Lookbook' }]}
      />

      {/* Filter rail */}
      {/* Sits directly under the 72px sticky header. */}
      <div className="sticky top-[72px] z-20 border-b border-royal-800/10 bg-cream-50/95 backdrop-blur-md">
        <div className="shell flex items-center gap-2 overflow-x-auto py-4 no-scrollbar">
          {tabs.map((t) => {
            const active = filter === t.slug;
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => setFilter(t.slug)}
                aria-pressed={active}
                className={`whitespace-nowrap border px-5 py-2.5 text-[11px] font-medium uppercase
                  tracking-wide2 transition-all duration-400 ease-luxe ${
                    active
                      ? 'border-royal-800 bg-royal-800 text-cream-50'
                      : 'border-royal-800/15 text-royal-800/70 hover:border-royal-800/40 hover:text-royal-900'
                  }`}
              >
                {t.title}
              </button>
            );
          })}
          <span className="ml-auto hidden shrink-0 pl-4 text-[11px] uppercase tracking-wide2 text-royal-700/45 sm:block">
            {visible.length} frames
          </span>
        </div>
      </div>

      {/* Story intros + grids */}
      {stories
        .filter((s) => filter === 'all' || filter === s.slug)
        .map((story, si) => {
          const photos = story.photos;
          return (
            <section
              key={story.slug}
              id={story.slug}
              className={`scroll-mt-32 py-16 sm:py-20 ${
                si % 2 === 0 ? 'bg-cream-50' : 'bg-cream-100'
              }`}
            >
              <div className="shell">
                <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
                  <Reveal className="lg:col-span-7">
                    <p className="eyebrow">{story.eyebrow}</p>
                    <h2 className="display-lg mt-3 text-royal-900">{story.title}</h2>
                    <Ornament className="mt-5 !justify-start" />
                    <p className="mt-6 max-w-xl lede">{story.text}</p>
                  </Reveal>
                  <Reveal delay={110} className="lg:col-span-5 lg:text-right">
                    <p className="font-display text-xl font-light italic text-maroon-700/80">
                      {story.subtitle}
                    </p>
                    <p className="mt-2 text-[11px] uppercase tracking-wide2 text-royal-700/50">
                      {photos.length} frames
                    </p>
                  </Reveal>
                </div>

                {/* Masonry — landscape frames span two columns */}
                <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                  {photos.map((photo, i) => {
                    const globalIndex = visible.findIndex((v) => v.src === photo.src);
                    return (
                      <Reveal
                        key={photo.src}
                        delay={(i % 4) * 70}
                        className={photo.wide ? 'col-span-2' : ''}
                      >
                        <button
                          type="button"
                          onClick={() => setLightbox(globalIndex)}
                          aria-label={`Open: ${photo.alt}`}
                          className="group relative block w-full overflow-hidden bg-royal-900
                            ring-1 ring-inset ring-royal-800/10 focus-visible:ring-gold-500"
                        >
                          <div
                            className={`overflow-hidden ${
                              photo.wide ? 'aspect-[3/2]' : 'aspect-[2/3]'
                            }`}
                          >
                            <img
                              src={photo.thumb}
                              alt={photo.alt}
                              loading={si === 0 && i < 4 ? 'eager' : 'lazy'}
                              decoding="async"
                              className="h-full w-full object-cover transition-transform
                                duration-[1300ms] ease-luxe group-hover:scale-[1.07]"
                            />
                          </div>
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 bg-gradient-to-t
                              from-royal-950/60 to-transparent opacity-0 transition-opacity
                              duration-500 group-hover:opacity-100"
                          />
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute bottom-3 right-3 grid h-9 w-9
                              place-items-center border border-gold-400/50 bg-royal-950/50
                              text-gold-300 opacity-0 transition-all duration-500 ease-luxe
                              group-hover:opacity-100"
                          >
                            <Icon.Arrow className="h-4 w-4 -rotate-45" />
                          </span>
                        </button>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}

      <Lightbox
        photos={visible}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndex={setLightbox}
      />

      <CtaBand
        eyebrow="Seen something?"
        title="Ask us which suite it was"
        text="Every frame here is a piece we hold or can remake. Send us the image and we will tell you what it is."
      />
    </>
  );
}
